import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { signOut } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import AddClientModal from './AddClientModal';

function Dashboard() {
  const user = auth.currentUser;
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'clients'), (snapshot) => {
      const clientData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setClients(clientData);
    });

    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <nav className="bg-blue-600 text-white p-4 flex justify-between items-center">
        <h1 className="text-xl font-bold">TheraVan Dashboard</h1>
        <button
          onClick={handleLogout}
          className="bg-white text-blue-600 px-4 py-2 rounded hover:bg-gray-200"
        >
          Logout
        </button>
      </nav>

      <main className="p-6">
        <h2 className="text-2xl font-semibold mb-2">👋 Welcome back!</h2>
        <Link
            to="/allcalendar"
            className="block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded mb-4"
        >
            🗓️Calendar View
        </Link>
        <p className="text-gray-700 mb-6">
          Logged in as: <strong>{user?.email}</strong>
        </p>
        <Link
            to="/account"
            className="text-blue-600 hover:underline mb-4 block"
        >
            👤 Account Portal
        </Link>

        <section className="bg-white p-4 rounded shadow-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">🧍 Client Records</h3>
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              ➕ Add New Client
            </button>
          </div>

          {/* 🔍 Filter + Sort */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
            <input
              type="text"
              placeholder="🔍 Search clients..."
              className="border px-3 py-2 rounded w-full md:w-1/2"
              onChange={(e) => setSearchTerm(e.target.value.toLowerCase())}
            />

            <select
              className="border px-3 py-2 rounded w-full md:w-1/4"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort by Name</option>
              <option value="createdAt">Sort by Date</option>
            </select>
          </div>

          {/* 🧾 Client List */}
          <div className="space-y-3">
            {clients
              .filter((client) =>
                (client.name?.toLowerCase()?.includes(searchTerm) || '') ||
                (client.email?.toLowerCase()?.includes(searchTerm) || '')
              )
              .sort((a, b) => {
                if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
                if (sortBy === 'createdAt') {
                  const aTime = a.createdAt?.seconds || 0;
                  const bTime = b.createdAt?.seconds || 0;
                  return bTime - aTime;
                }
                return 0;
              })
              .map((client) => (
                <Link key={client.id} to={`/client/${client.id}`}>
                  <div className="p-4 bg-gray-50 hover:bg-gray-100 rounded border cursor-pointer">
                    <h4 className="text-lg font-bold">{client.name}</h4>
                    <p className="text-sm text-gray-600">{client.email}</p>
                  </div>
                </Link>
              ))}
          </div>
        </section>
      </main>

      {showModal && <AddClientModal onClose={() => setShowModal(false)} />}
    </div>
  );
}

export default Dashboard;
